import React, { memo, useMemo, useCallback, useState, useContext, useEffect } from 'react';
import {
    Box,
    Alert,
    Typography,
    Button,
    Grid,
    Fade,
    Grow,
    CircularProgress,
    Stack,
    Paper,
    InputBase,
    Divider,
    IconButton,
    Tooltip,
    Snackbar
} from '@mui/material';
import {
    QuestionAnswerOutlined as QuestionAnswerOutlinedIcon,
    TableRowsRounded as TableRowsRoundedIcon,
    Send as SendIcon,
    BorderColor,
    ContentCopy as ContentCopyIcon,
    BarChartOutlined as BarChartOutlinedIcon,
    QueryStats as QueryStatsIcon,
    DashboardOutlined as DashboardOutlinedIcon,
    ThumbUpAltOutlined as ThumbUpAltOutlinedIcon,
    ThumbDownAltOutlined as ThumbDownAltOutlinedIcon
} from '@mui/icons-material';

import ChatStreamSimulator from '../utils/ChatStreamSimulator.js'; // API Dynamo
import TableView from './TableView.js';
import MyChart from './MyChart.js';
import AnswerDetailsDialog from './AnswerDetailsDialog.js';
import UpgradePlanBanner from './UpgradePlanBanner.js';
import PlanComparisonModal from './PlanComparisonModal.js';
import { DataContext } from '../context/DataContext';
import CreditsEngineService from '../services/CreditsEngineService';
import DeepAnalysisReport from './deep-analysis/DeepAnalysisReport';
import DeepAnalysisProgress from './deep-analysis/DeepAnalysisProgress';
import DashboardView from './dashboard/DashboardView';
import DashboardProgress from './dashboard/DashboardProgress';
import ChatModelSelector from './ChatModelSelector';
import { answerCreditsLabel } from '../utils/answerCredits';
import ChatModeSelector from './ChatModeSelector';
import useMediaQuery from '@mui/material/useMediaQuery';
import { describeModel } from '../utils/llmModels';

// Constantes extraídas
const STYLES = {
    errorAlert: {
        position: "fixed",
        width: "80%",
        top: "65px",
        left: "20%",
        marginLeft: "-10%"
    },
    answerContainer: {
        borderRadius: 4,
        pl: 1, pr: 1, pt: 1,
        display: 'flex',
        alignItems: 'left'
    },
    logoBox: {
        pr: 1, pl: 0.5
    },
    userQueryBox: {
        textAlign: "right",
        borderRadius: 4,
        fontWeight: 500,
        pt: 1, pb: 1, pl: 2, pr: 2, mb: 1, mt: 2, mr: 1,
        boxShadow: 'var(--wb-x-shadow-0000000d, rgba(0, 0, 0, 0.05)) 0px 4px 12px',
        background: "var(--wb-blue-google)",
        display: 'flex',
        alignItems: 'center',
        gap: 1
    },
    chartContainer: (theme) => ({
        background: "var(--wb-surface-white)", pt: 0, pb: 2, pl: 2, pr: 2, m: 0, borderRadius: 4,
        boxShadow: 'var(--wb-x-shadow-0000000d, rgba(0, 0, 0, 0.05)) 0px 4px 12px',
        bgcolor: 'var(--wb-x-bg-f8fffc66, rgba(248, 255, 252, 0.4))'
    }),

    inputPaper: {
        px: 2,
        py: 1,
        mb: 2,
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        gap: 1,
        backgroundColor: 'var(--wb-x-bg-1b2535, #1B2535)', // gris más claro
        borderRadius: 8,
        border: '1px solid var(--wb-x-border-ffffff33, #ffffff33)', // contorno blanco semitransparente
        boxShadow: 'none',
    },
    inputRow: {
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        gap: 0.5
    },
    quickActionsContainer: {
        display: 'flex',
        gap: 0.5,
        flexWrap: 'wrap',
        pt: 0.5
    },

    loadingBox: {
        pt: 1, pb: 1, pl: 2, pr: 2, m: 0, borderRadius: 4, display: "flex",
        boxShadow: 'var(--wb-x-shadow-f5f2f20d, rgba(245, 242, 242, 0.05)) 0px 4px 12px',
        background: "var(--wb-blue-google)",
        color: "var(--wb-x-text-fcfafa, #fcfafaff)",
    }
};

// Componente separado para el texto simulado. `instant`: true para
// respuestas cargadas desde historial (se muestran completas de una vez);
// false (default) para una respuesta recién generada, que se escribe
// carácter por carácter en un solo flujo continuo (estilo ChatGPT), no
// línea por línea en paralelo. `markdown`: true en ChatStreamSimulator --
// la respuesta del agente llega en Markdown plano (CLAUDE.md: "su
// `completion` es texto plano con marcadores Markdown, y el frontend antes
// lo renderizaba tal cual"); acá se formatea (negritas, listas, tablas,
// código) en vez de mostrar los símbolos `**`/`#`/`|` literales.
// `component="div"` (no el `<p>` que Typography usa por default): Markdown
// renderiza sus propios elementos de bloque (p, ul, h2...), que no son
// válidos anidados dentro de un `<p>`.
const ChatTextRenderer = memo(({ text, instant = false }) => {
    return (
        <Typography component="div" variant="body1" sx={{ color: "var(--wb-x-text-a0a0a0, #a0a0a0)" }}>
            <ChatStreamSimulator message={text} typingSpeed={8} instant={instant} markdown />
        </Typography>
    );
});

// Botón reutilizable para copiar texto al portapapeles
const CopyToClipboardButton = memo(({ text, ariaLabel, iconColor = 'var(--wb-blue-google)' }) => {
    const handleCopy = useCallback(async () => {
        if (!text) return;

        const value = typeof text === 'string' ? text : String(text ?? '');
        const clipboard = typeof navigator !== 'undefined' ? navigator.clipboard : null;

        try {
            if (clipboard?.writeText) {
                await clipboard.writeText(value);
                return;
            }
            throw new Error('Clipboard API not available');
        } catch (error) {
            console.error('No se pudo copiar el texto:', error);
            if (typeof document === 'undefined') {
                return;
            }
            const textarea = document.createElement('textarea');
            textarea.value = value;
            textarea.setAttribute('readonly', '');
            textarea.style.position = 'absolute';
            textarea.style.left = '-9999px';
            document.body.appendChild(textarea);
            textarea.select();
            try {
                document.execCommand('copy');
            } catch (fallbackError) {
                console.error('Fallo el método alterno de copiado:', fallbackError);
            } finally {
                document.body.removeChild(textarea);
            }
        }
    }, [text]);

    const isDisabled = !text || (typeof text === 'string' && text.trim() === '');

    return (
        <Tooltip title="Copiar" arrow>
            <span>
                <IconButton
                    size="small"
                    onClick={handleCopy}
                    disabled={isDisabled}
                    aria-label={ariaLabel}
                    sx={{ color: iconColor }}
                >
                    <ContentCopyIcon fontSize="inherit" />
                </IconButton>
            </span>
        </Tooltip>
    );
});

// Componente separado para los botones de navegación
const TabNavigationButtons = memo(({
    answer,
    controlAnswers,
    index,
    handleShowTab
}) => {
    if (!answer.queryResults?.length) return null;

    return (
        <Fade timeout={1000} in={answer.queryResults.length > 0}>

            <div>
                <Button
                    onClick={handleShowTab(index, "answer")}
                    startIcon={<QuestionAnswerOutlinedIcon />}
                    sx={{
                        color: controlAnswers[index].current_tab_view === 'answer' ? 'var(--wb-on-primary)' : 'var(--wb-x-text-cccccc, #ccc)',
                        backgroundColor: controlAnswers[index].current_tab_view === 'answer' ? 'var(--wb-x-bg-055dff, #055dffff)' : 'transparent',
                        '&:hover': {
                            backgroundColor: controlAnswers[index].current_tab_view === 'answer' ? 'var(--wb-x-bg-2509a3, #2509a3ff)' : 'var(--wb-x-bg-160565b7, #160565b7)'
                        },
                        mr: 1,
                        borderRadius: 2,
                        textTransform: 'none',
                        px: 2
                    }}
                >
                    Respuesta
                </Button>
                <Button
                    onClick={handleShowTab(index, "records")}
                    startIcon={<TableRowsRoundedIcon />}
                    sx={{
                        color: controlAnswers[index].current_tab_view === 'records' ? 'var(--wb-on-primary)' : 'var(--wb-x-text-cccccc, #ccc)',
                        backgroundColor: controlAnswers[index].current_tab_view === 'records' ? 'var(--wb-x-bg-055dff, #055dffff)' : 'transparent',
                        '&:hover': {
                            backgroundColor: controlAnswers[index].current_tab_view === 'records' ? 'var(--wb-x-bg-2509a3, #2509a3ff)' : 'var(--wb-x-bg-160565b7, #160565b7)'
                        },
                        mr: 1,
                        borderRadius: 2,
                        textTransform: 'none',
                        px: 2
                    }}
                >
                    Registros
                </Button>
            </div>
        </Fade>
    );
});

// Componente separado para la vista de registros
const RecordsView = memo(({ queryResults, isVisible }) => {
    if (!queryResults?.length) return null;

    return (
        <Grow in={isVisible}>
            <Box style={{ display: isVisible ? "block" : "none" }} sx={{ pt: 2 }}>
                {queryResults.map((query_result, x) => (
                    <Box key={`table_${x}`}>
                        {query_result.query_results.length > 0 ? (
                            <TableView query_results={query_result.query_results} />
                        ) : (
                            <Typography sx={{ textAlign: "center" }}>Sin registros de datos</Typography>
                        )}
                        <Typography
                            component="div"
                            variant="body1"
                            sx={{
                                fontSize: "0.85rem",
                                pl: 2, pr: 2, pt: 1, pb: 1, m: 0,
                                background: "var(--wb-x-bg-898484, #898484ff)",
                                color: 'var(--wb-x-text-303030, #303030ff)',
                                borderBottomRightRadius: 16,
                                borderBottomLeftRadius: 16,
                                mb: x === queryResults.length - 1 ? 1 : 4,
                                boxShadow: 'var(--wb-x-shadow-0000000d, rgba(0, 0, 0, 0.05)) 0px 4px 12px'
                            }}
                        >
                            <strong>Query:</strong> {query_result.query}
                        </Typography>
                    </Box>
                ))}
            </Box>
        </Grow>
    );
});

// Componente separado para la vista del gráfico (se muestra siempre debajo de la respuesta, no en pestaña aparte)
const ChartView = memo(({ chart }) => {
    if (!chart?.chart_type) return null;

    return (
        <Grow in={true} timeout={600}>
            <Box sx={{ pt: 2 }}>
                {/* Color explícito: heredado salía blanco y no se leía en el tema claro */}
                <Typography variant="body1" sx={{ pb: 2, color: 'var(--wb-text)' }}>
                    {chart.caption.split("\n").map((item, idx) => (
                        <span key={idx}>
                            {item}
                            <br />
                        </span>
                    ))}
                </Typography>
                <Box sx={STYLES.chartContainer}>
                    <MyChart
                        options={chart.chart_configuration.options}
                        series={chart.chart_configuration.series}
                        type={chart.chart_type}
                    />
                </Box>
            </Box>
        </Grow>
    );
});

// Componente principal para cada respuesta
const QUESTION_ERROR_ACTIONS = {
    plans: 'Ver planes',
    connections: 'Conectar datos'
};

const QuestionErrorBubble = ({ answer, onAction }) => {
    const action = answer.error?.action;
    return (
        <Box sx={STYLES.answerContainer}>
            <Box sx={STYLES.logoBox}>
                <img src="/images/logo_32_x_40.png" alt="WhisperBI" width={32} height={40} />
            </Box>
            <Box
                role="alert"
                sx={{
                    flexGrow: 1,
                    mb: 1,
                    px: 2,
                    py: 1.5,
                    borderRadius: 2,
                    border: '1px solid var(--wb-warning-90)',
                    borderLeft: '3px solid var(--wb-warning-strong)',
                    backgroundColor: 'var(--wb-warning-12)'
                }}
            >
                <Typography variant="body1" sx={{ color: 'var(--wb-text-strong)' }}>
                    {answer.text}
                </Typography>
                {action && QUESTION_ERROR_ACTIONS[action] && onAction && (
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={() => onAction(action)}
                        sx={{
                            mt: 1,
                            textTransform: 'none',
                            borderRadius: 2,
                            color: 'var(--wb-warning)',
                            borderColor: 'var(--wb-warning-90)',
                            '&:hover': { borderColor: 'var(--wb-warning-strong)', backgroundColor: 'var(--wb-warning-12)' }
                        }}
                    >
                        {QUESTION_ERROR_ACTIONS[action]}
                    </Button>
                )}
            </Box>
        </Box>
    );
};

const AnswerItem = memo(({
    answer,
    index,
    controlAnswers,
    handleShowTab,
    onFeedbackSelect,
    onFollowUp,
    onSaveDashboard,
    onOpenDashboard,
    onQuestionErrorAction,
    isLive = false
}) => {
    const currentTab = controlAnswers[index]?.current_tab_view;
    const isPositiveFeedback = answer.is_response_ok === true;
    const isNegativeFeedback = answer.is_response_ok === false;

    const handleThumbUp = useCallback(() => {
        console.log('Manita arriba presionada');
        onFeedbackSelect?.(index, true);
    }, [index, onFeedbackSelect]);

    const handleThumbDown = useCallback(() => {
        console.log('Manita abajo presionada');
        onFeedbackSelect?.(index, false);
    }, [index, onFeedbackSelect]);

    // Render para respuesta del usuario
    if (!answer.hasOwnProperty("text")) {
        return (
            <Grid container justifyContent="flex-end">
                <Box sx={STYLES.userQueryBox}>
                    <Typography variant="body1" sx={{ color: 'var(--wb-text-strong)', flexGrow: 1 }}>
                        {answer.query}
                    </Typography>
                    <CopyToClipboardButton
                        text={answer.query}
                        ariaLabel="Copiar pregunta"
                        iconColor="var(--wb-text-strong)"
                    />
                </Box>
            </Grid>
        );
    }

    // Error de la pregunta (sin créditos, límite, plan…): en la conversación,
    // sin pestañas ni calificación.
    if (answer.error) {
        return <QuestionErrorBubble answer={answer} onAction={onQuestionErrorAction} />;
    }

    // Render para respuesta del sistema
    return (
        <Box sx={STYLES.answerContainer}>
            <Box sx={STYLES.logoBox}>
                <img
                    src="/images/logo_32_x_40.png"
                    alt="WhisperBI"
                    width={32}
                    height={40}
                />
            </Box>
            <Box sx={{ p: 0 }}>

                {/* Vista de respuesta de texto */}
                <Grow in={currentTab === 'answer'}>
                    <Box
                        id={'answer' + index}
                        style={{ display: currentTab === 'answer' ? "block" : "none" }}
                    >
                        <Stack direction="row" spacing={1} alignItems="flex-start">
                            <Box sx={{ flexGrow: 1 }}>
                                {answer.dashboard ? (
                                    <DashboardView
                                        dashboard={answer.dashboard}
                                        onSave={onSaveDashboard ? () => onSaveDashboard(index) : undefined}
                                        saving={Boolean(answer._dashboardSaving)}
                                        savedId={answer.savedDashboardId || null}
                                        saveError={answer._dashboardSaveError || null}
                                        onOpenSaved={onOpenDashboard}
                                    />
                                ) : answer.deepReport ? (
                                    <DeepAnalysisReport report={answer.deepReport} onFollowUp={onFollowUp} />
                                ) : (
                                    <ChatTextRenderer text={answer.text} instant={!isLive} />
                                )}
                                <Stack
                                    direction="row"
                                    spacing={0.5}
                                    sx={{
                                        pt: 1,
                                        pb: 1,
                                        borderLeft: '3px solid var(--wb-x-border-ff9800, #ff9800)',
                                        pl: 1.5,
                                        mb: 1
                                    }}
                                >
                                    <IconButton
                                        size="small"
                                        onClick={handleThumbUp}
                                        aria-label="Manita arriba"
                                        aria-pressed={isPositiveFeedback}
                                        sx={{
                                            color: isPositiveFeedback ? 'var(--wb-on-primary)' : 'var(--wb-x-text-cccccc, #ccc)',
                                            backgroundColor: isPositiveFeedback ? 'var(--wb-x-bg-055dff, #055dffff)' : 'transparent',
                                            '&:hover': {
                                                backgroundColor: isPositiveFeedback ? 'var(--wb-x-bg-2509a3, #2509a3ff)' : 'var(--wb-x-bg-160565b7, #160565b7)'
                                            },
                                            borderRadius: 2,
                                            p: 0.75
                                        }}
                                    >
                                        <ThumbUpAltOutlinedIcon fontSize="inherit" />
                                    </IconButton>
                                    <IconButton
                                        size="small"
                                        onClick={handleThumbDown}
                                        aria-label="Manita abajo"
                                        aria-pressed={isNegativeFeedback}
                                        sx={{
                                            color: isNegativeFeedback ? 'var(--wb-on-primary)' : 'var(--wb-x-text-cccccc, #ccc)',
                                            backgroundColor: isNegativeFeedback ? 'var(--wb-x-bg-055dff, #055dffff)' : 'transparent',
                                            '&:hover': {
                                                backgroundColor: isNegativeFeedback ? 'var(--wb-x-bg-2509a3, #2509a3ff)' : 'var(--wb-x-bg-160565b7, #160565b7)'
                                            },
                                            borderRadius: 2,
                                            p: 0.75
                                        }}
                                    >
                                        <ThumbDownAltOutlinedIcon fontSize="inherit" />
                                    </IconButton>
                                </Stack>
                            </Box>
                            <CopyToClipboardButton
                                text={answer.text}
                                ariaLabel="Copiar respuesta"
                            />
                        </Stack>

                        {answer.modelId && (
                            <Typography variant="caption" color="secondaryText" sx={{ display: 'block', pt: 0.5 }}>
                                Respondió {describeModel({ model: answer.modelId }).name}
                            </Typography>
                        )}

                        {/* Gráfica generada para esta respuesta: siempre debajo del texto, no en pestaña aparte */}
                        {(answer.chart === "loading" || answer.chartLoading) && (
                            <Box sx={{ display: 'flex', alignItems: 'center', pt: 2 }}>
                                <CircularProgress size={16} color="primary" sx={{ p: 0, m: 0 }} />
                                <Typography variant="caption" color="secondaryText" sx={{ pl: 2 }}>
                                    Generando gráfica...
                                </Typography>
                            </Box>
                        )}

                        <ChartView chart={answer.chart} />

                        {answer.chart?.rationale && (
                            <Typography variant="caption" color="secondaryText" sx={{ display: 'block', pt: 1 }}>
                                {answer.chart.rationale}
                            </Typography>
                        )}

                        {answer.chartError && !answer.chart && (
                            <Typography variant="caption" color="secondaryText" sx={{ display: 'block', pt: 1 }}>
                                {answer.chartError}
                            </Typography>
                        )}

                        {/* Total de créditos de la pregunta (turno + gráfica), al final de la respuesta */}
                        {answerCreditsLabel(answer) && (
                            <Typography variant="caption" color="secondaryText" sx={{ display: 'block', pt: 1 }}>
                                {answerCreditsLabel(answer)}
                            </Typography>
                        )}
                    </Box>
                </Grow>

                {/* Vistas de registros */}
                <RecordsView
                    queryResults={answer.queryResults}
                    isVisible={currentTab === 'records'}
                />

                {/* Controles de navegación (Respuesta / Registros) */}
                {answer.hasOwnProperty('chart') && (
                    <Grid container spacing={0} sx={{ display: 'flex', alignItems: 'center' }}>
                        <Grid>
                            <TabNavigationButtons
                                answer={answer}
                                controlAnswers={controlAnswers}
                                index={index}
                                handleShowTab={handleShowTab}
                            />
                        </Grid>
                    </Grid>
                )}
            </Box>
        </Box>
    );
});

// Componente principal optimizado
const OptimizedChatComponent = ({
    errorMessage,
    setErrorMessage,
    answers,
    controlAnswers,
    handleShowTab,
    loading,
    progressLabel = "",
    historyLoading,
    scrollRef,
    messagesRef,
    query,
    handleQuery,
    handleKeyPress,
    enabled,
    handleClick,
    selectedAB,
    openAnswerDetails,
    handleCloseAnswerDetails,
    selectedB,
    WELCOME_MESSAGE,
    MAX_LENGTH_INPUT_SEARCH,
    onFeedbackSelect,
    selectedWorkspace,
    workspaceError,
    historyBoundaryIndex = 0,
    hasMoreHistory = false,
    loadingMoreHistory = false,
    onLoadMoreHistory,
    deepProgress = null,
    dashboardProgress = null,
    analysisMode,
    onAnalysisModeChange,
    selectedModelId = null,
    onModelChange,
    plansModalRequest = 0,
    onFollowUp,
    onSaveDashboard,
    onOpenDashboard,
    onQuestionErrorAction
}) => {

    // Modo de análisis: lo controla Chat.js (lo envía al backend); el estado
    // local solo cubre usos sin ese control.
    const [localQuickAction, setLocalQuickAction] = useState('basic');
    const selectedQuickAction = analysisMode ?? localQuickAction;
    const setSelectedQuickAction = onAnalysisModeChange || setLocalQuickAction;

    // --- Upgrade de plan (ver plan de migración "Upgrade de plan en el
    // chat con cobro real vía Stripe") ---
    const { accountId, isPlatformRoot, isTenantAdmin } = useContext(DataContext);
    const [planInfo, setPlanInfo] = useState(null);
    const [plansModalOpen, setPlansModalOpen] = useState(false);
    const [checkoutToast, setCheckoutToast] = useState({ open: false, message: '', severity: 'success' });

    const refetchPlanInfo = useCallback(async () => {
        if (!accountId) return;
        try {
            const response = await CreditsEngineService.getLimits({ accountId });
            setPlanInfo(response?.account_policy || null);
        } catch (err) {
            console.error('No se pudo obtener la información del plan actual', err);
        }
    }, [accountId]);

    useEffect(() => {
        refetchPlanInfo();
    }, [refetchPlanInfo]);

    // Retorno desde Stripe Checkout (success_url/cancel_url apuntan de
    // vuelta acá con ?checkout=success|cancel) -- el webhook puede tardar
    // unos segundos en aplicar el cambio de plan, así que se reintenta una
    // vez tras un breve delay en vez de exigir consistencia instantánea.
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const checkoutStatus = params.get('checkout');
        if (checkoutStatus === 'success') {
            setCheckoutToast({ open: true, message: 'Pago recibido. Tu plan se actualizará en unos segundos.', severity: 'success' });
            setTimeout(refetchPlanInfo, 3000);
        } else if (checkoutStatus === 'cancel') {
            setCheckoutToast({ open: true, message: 'Actualización de plan cancelada.', severity: 'info' });
        }
        if (checkoutStatus) {
            params.delete('checkout');
            const newSearch = params.toString();
            window.history.replaceState({}, '', `${window.location.pathname}${newSearch ? `?${newSearch}` : ''}`);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Free/desconocido -> tratados como plan gratis (bloquea hasta confirmar
    // un plan pago real, en vez de mostrar habilitado y luego deshabilitar).
    const isFreePlan = !(planInfo && Number(planInfo.plan_monthly_price) > 0);

    const quickActions = useMemo(() => ([
        { key: 'basic', label: 'Análisis Básico', Icon: BarChartOutlinedIcon, requiresPaidPlan: false },
        { key: 'deep', label: 'Análisis Profundo', Icon: QueryStatsIcon, requiresPaidPlan: true },
        { key: 'dashboard', label: 'Dashboard', Icon: DashboardOutlinedIcon, requiresPaidPlan: true }
    ]), []);
    // En teléfonos los tres botones de modo ocupan una fila entera: ahí van
    // como menú desplegable (ChatModeSelector), junto al selector de modelo.
    const isPhone = useMediaQuery((theme) => theme.breakpoints.down('sm'), { noSsr: true });

    // Modelos que se pueden elegir en el chat: los de "Modelos configurados"
    // (vigentes y activos). Si la lista no carga, el selector no se muestra y
    // todo sigue con el modelo del agente.
    const [chatModels, setChatModels] = useState([]);
    useEffect(() => {
        let cancelled = false;
        CreditsEngineService.listConfiguredModels()
            .then((configured) => {
                if (!cancelled) {
                    // available_in_plan: el plan de la cuenta lo permite (Free
                    // no incluye Opus/Fable por defecto). El selector deshabilita
                    // los demás; el bloqueo real lo hace credits-engine.
                    setChatModels(configured.map((model) => ({
                        ...describeModel(model),
                        availableInPlan: model.available_in_plan !== false
                    })));
                }
            })
            .catch((err) => console.error('No se pudo obtener la lista de modelos para el chat', err));
        return () => { cancelled = true; };
    }, []);

    // Un modelo elegido que ya no está configurado (root lo desactivó) o que
    // el plan ya no incluye vuelve al del agente en vez de fallar en la
    // próxima pregunta.
    useEffect(() => {
        if (selectedModelId && chatModels.length > 0 && onModelChange
            && !chatModels.some((model) => model.id === selectedModelId && model.availableInPlan)) {
            onModelChange(null);
        }
    }, [chatModels, selectedModelId, onModelChange]);

    const handleQuickActionSelect = useCallback((actionKey) => {
        setSelectedQuickAction(actionKey);
    }, [setSelectedQuickAction]);

    // El backend respondió plan_required (Chat.js incrementa el contador).
    useEffect(() => {
        if (plansModalRequest > 0) {
            setPlansModalOpen(true);
        }
    }, [plansModalRequest]);

    // Si el plan deja de ser de pago (o aún no se confirmó) con un modo de
    // pago elegido, volver al básico: el botón queda deshabilitado y el
    // backend igual lo rechazaría.
    useEffect(() => {
        const action = quickActions.find((item) => item.key === selectedQuickAction);
        if (isFreePlan && planInfo && action?.requiresPaidPlan) {
            setSelectedQuickAction('basic');
        }
    }, [isFreePlan, planInfo, quickActions, selectedQuickAction, setSelectedQuickAction]);
    // Memoización de componentes costosos. `answers` ya viene acotado por
    // Chat.js (paginación real contra el backend -- ver getConversation/
    // handleLoadMoreHistory), así que acá se pinta todo tal cual llega, sin
    // ventana local. `key`: usa `answer._key` (estable, asignado por
    // Chat.js) en vez del índice -- "cargar más" antepone turnos viejos al
    // principio del array y desplaza el índice de TODO lo que ya estaba
    // montado; con una key basada en índice, React reconciliaría eso como
    // "se fueron estos ítems, llegaron estos otros" y remontaría (perdiendo
    // el efecto de escritura ya completado) los turnos recientes cada vez
    // que se carga más historial. Con `_key` estable, React los reconoce
    // como el mismo nodo y solo monta (con fade-in) los turnos realmente
    // nuevos -- el efecto propuesto para la carga de historial.
    const answersListMemoized = useMemo(() => (
        answers.map((answer, index) => (
            <li key={answer._key || `answer_${index}`}>
                <Fade in timeout={400}>
                    <div>
                        <AnswerItem
                            answer={answer}
                            index={index}
                            controlAnswers={controlAnswers}
                            handleShowTab={handleShowTab}
                            onFeedbackSelect={onFeedbackSelect}
                            onFollowUp={onFollowUp}
                            onSaveDashboard={onSaveDashboard}
                            onOpenDashboard={onOpenDashboard}
                            onQuestionErrorAction={onQuestionErrorAction}
                            isLive={index >= historyBoundaryIndex}
                        />
                    </div>
                </Fade>
            </li>
        ))
    ), [answers, controlAnswers, handleShowTab, onFeedbackSelect, onFollowUp, onSaveDashboard, onOpenDashboard, onQuestionErrorAction, historyBoundaryIndex]);

    // Callbacks optimizados
    const handleErrorClose = useCallback(() => {
        setErrorMessage("");
    }, [setErrorMessage]);

    const sendIconColor = useMemo(() =>
        enabled ? 'var(--wb-blue-google)' : 'var(--wb-x-text-bdbdbd, #bdbdbd)'
        , [enabled]);

    return (
        // Ocupa el alto que le deja LayoutApp (columna flex de 100dvh): solo
        // la lista de mensajes hace scroll, la página nunca.
        <Box sx={{
            pl: 2, pr: 2, pt: 0, pb: 0,
            position: 'relative',
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
        }}>

            {/* Header (la selección de workspace vive en el menú lateral) */}
            <Paper
                elevation={2}
                sx={{
                    // Una sola fila compacta: workspace a la izquierda, plan a la derecha.
                    px: 2,
                    py: 0.5,
                    mb: 2,
                    flexShrink: 0,
                    borderRadius: 2,
                    backgroundColor: 'var(--wb-x-bg-1b2535, #1B2535)',
                    border: '1px solid var(--wb-x-border-ffffff33, #ffffff33)',
                    // Teléfono en horizontal: el espacio es para los mensajes.
                    // El error de workspace sí se muestra siempre.
                    ...(!workspaceError && {
                        '@media (max-height: 500px)': { display: 'none' },
                    }),
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minHeight: 28 }}>
                    {/* Display current workspace and agent info */}
                    {selectedWorkspace && (
                        <Typography
                            variant="body2"
                            noWrap
                            sx={{ color: 'var(--wb-text-strong)', fontWeight: 500, minWidth: 0 }}
                        >
                            {selectedWorkspace.name || 'Sin nombre'}
                        </Typography>
                    )}
                    <Box sx={{ ml: 'auto', flexShrink: 0 }}>
                        <UpgradePlanBanner
                            planName={planInfo?.plan_name}
                            onOpenPlans={() => setPlansModalOpen(true)}
                        />
                    </Box>
                </Box>

                {/* Display workspace error */}
                {workspaceError && (
                    <Alert severity="error" sx={{ mt: 1 }}>
                        {workspaceError}
                    </Alert>
                )}
            </Paper>

            {historyLoading && (
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        zIndex: 20,
                        backdropFilter: 'blur(4px)',
                        background: 'linear-gradient(135deg, var(--wb-x-bg-0f172aeb, rgba(15,23,42,0.92)), var(--wb-x-bg-1e40afa6, rgba(30,64,175,0.65)))',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexDirection: 'column',
                        gap: 2,
                        px: 3
                    }}
                >
                    <CircularProgress size={36} sx={{ color: 'var(--wb-accent)' }} />
                    <Typography
                        variant="body1"
                        sx={{ color: 'var(--wb-text)', fontWeight: 500, textAlign: 'center' }}
                    >
                        Recuperando conversación...
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{ color: 'var(--wb-x-text-e2e8f0b2, rgba(226, 232, 240, 0.7))', textAlign: 'center', maxWidth: 320 }}
                    >
                        Estamos preparando el historial de mensajes para que retomes tu análisis justo donde lo dejaste.
                    </Typography>
                </Box>
            )}

            {/* Alert de error */}
            {errorMessage && (
                <Alert
                    severity="error"
                    sx={STYLES.errorAlert}
                    onClose={handleErrorClose}
                >
                    {errorMessage}
                </Alert>
            )}

            {/* Contenedor principal del chat */}
            <Box
                id="chatHelper"
                ref={messagesRef}
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    flex: 1,
                    // Sin minHeight: 0 el hijo flex crece con su contenido
                    // y el scroll vuelve a la página.
                    minHeight: 0,
                    overflowX: "hidden",
                    overflowY: "auto",
                }}
            >
                {(answers.length > 0 || loading) ? (
                    <ul style={{ paddingBottom: 14 }}>
                        {hasMoreHistory && (
                            <li style={{ listStyle: 'none', textAlign: 'center', marginBottom: 12 }}>
                                <Button
                                    onClick={onLoadMoreHistory}
                                    disabled={loadingMoreHistory}
                                    size="small"
                                    startIcon={loadingMoreHistory ? <CircularProgress size={14} sx={{ color: 'var(--wb-x-text-9db8e8, #9db8e8)' }} /> : null}
                                    sx={{
                                        color: 'var(--wb-x-text-9db8e8, #9db8e8)',
                                        textTransform: 'none',
                                        borderRadius: 2,
                                        border: '1px solid var(--wb-x-border-ffffff33, #ffffff33)',
                                        '&:hover': { backgroundColor: 'var(--wb-x-bg-160565b7, #160565b7)' }
                                    }}
                                >
                                    {loadingMoreHistory ? 'Cargando...' : 'Cargar historial anterior (5 preguntas más)'}
                                </Button>
                            </li>
                        )}
                        {answersListMemoized}

                        {/* Indicador de carga */}
                        {loading && dashboardProgress && (
                            <Box sx={{ p: 0, pl: 1 }}>
                                <Fade timeout={600} in={loading}>
                                    <Box><DashboardProgress progress={dashboardProgress} /></Box>
                                </Fade>
                            </Box>
                        )}
                        {loading && deepProgress && (
                            <Box sx={{ p: 0, pl: 1 }}>
                                <Fade timeout={600} in={loading}>
                                    <Box><DeepAnalysisProgress progress={deepProgress} /></Box>
                                </Fade>
                            </Box>
                        )}
                        {loading && !deepProgress && !dashboardProgress && (
                            <Box sx={{ p: 0, pl: 1 }}>
                                <Fade timeout={1000} in={loading}>
                                    <Stack direction="row" spacing={2} sx={{ display: "flex", alignItems: "center" }}>
                                        {/* `color="primary"` resuelve al negro del tema (theme.js) --
                                            invisible sobre el fondo oscuro del panel de chat (#1B2535
                                            en LayoutApp.js). Mismo motivo por el que el overlay de
                                            "Recuperando conversación..." de arriba ya fuerza un color
                                            explícito en vez de dejar el default del tema. */}
                                        <CircularProgress size={28} sx={{ color: 'var(--wb-blue-google)', p: 0, m: 0 }} />
                                        <Box sx={STYLES.loadingBox}>
                                            <Typography variant="body1" sx={{ color: 'var(--wb-text-strong)' }}>
                                                {progressLabel || 'Respondiendo a tu pregunta...'}
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </Fade>
                            </Box>
                        )}

                        <li ref={scrollRef} />
                    </ul>
                ) : (
                    // Estado vacío
                    <Box
                        textAlign="center"
                        sx={{
                            pl: 1, pt: 1, pr: 1, pb: 6,
                            flexGrow: 1,
                            display: "flex",
                            alignItems: "flex-end"
                        }}
                    >
                        <div style={{ width: "100%" }}>
                            <img
                                src="/images/logo_100_x_130.png"
                                alt="Whisperer"
                                width={100}
                                height={130}
                            />
                            <Typography sx={{ pb: 4, fontWeight: 400 }}
                            color={'var(--wb-text-strong)'}
                            >
                                Simplifico el analisis de datos
                            </Typography>
                            <Box
                                sx={{
                                    display: 'inline-block',
                                    backgroundColor: 'var(--wb-blue-google)', // azul moderno
                                    color: 'var(--wb-on-primary)',
                                    borderRadius: '16px',
                                    px: 3,
                                    py: 1.5,
                                    mb: 3,
                                    boxShadow: '0 2px 10px var(--wb-x-shadow-00000033, rgba(0,0,0,0.2))',
                                    fontWeight: '500'
                                }}
                            >
                                {WELCOME_MESSAGE}
                            </Box>
                            <Stack direction="row" spacing={2} justifyContent="center">
                                <Button variant="contained" sx={{ background: 'var(--wb-x-bg-2f3c48, #2f3c48)', color: 'var(--wb-x-text-cccccc, #ccc)', borderRadius: '20px', textTransform: 'none' }}>
                                    Ver resumen de ventas
                                </Button>
                                <Button variant="contained" sx={{ background: 'var(--wb-x-bg-2f3c48, #2f3c48)', color: 'var(--wb-x-text-cccccc, #ccc)', borderRadius: '20px', textTransform: 'none' }}>
                                    Análisis por
                                </Button>
                                <Button variant="contained" sx={{ background: 'var(--wb-x-bg-2f3c48, #2f3c48)', color: 'var(--wb-x-text-cccccc, #ccc)', borderRadius: '20px', textTransform: 'none' }}>
                                    Alertas de rendimiento
                                </Button>
                            </Stack>
                        </div>
                    </Box>
                )}
            </Box>

            {/* Input de consulta */}
            <Paper component="form" sx={STYLES.inputPaper}>
                <Box sx={STYLES.inputRow}>
                    <InputBase
                        required
                        id="query"
                        name="query"
                        placeholder="Escribe tu pregunta..."
                        fullWidth
                        multiline
                        minRows={2}
                        maxRows={2}
                        onChange={handleQuery}
                        onKeyDown={handleKeyPress}
                        value={query}
                        inputProps={{ maxLength: MAX_LENGTH_INPUT_SEARCH }}
                        sx={{
                            flexGrow: 1,
                            color: 'var(--wb-text-strong)',
                            fontSize: '0.95rem',
                            '::placeholder': {
                                color: 'var(--wb-x-text-bbbbbb, #bbbbbb)'
                            }
                        }}
                    />
                    <Divider orientation="vertical" flexItem sx={{ height: 'auto', ml: 1, mr: 1 }} />
                    <IconButton
                        color="primary"
                        sx={{ p: 0.5, mr: 0.5 }}
                        aria-label="buscar"
                        disabled={!enabled}
                        onClick={handleClick}
                        type="button"
                    >
                        <SendIcon sx={{ color: sendIconColor }} />
                    </IconButton>
                </Box>
                <Box sx={STYLES.quickActionsContainer}>
                    {isPhone ? (
                        <ChatModeSelector
                            actions={quickActions.map((action) => ({ ...action, locked: action.requiresPaidPlan && isFreePlan }))}
                            value={selectedQuickAction}
                            onChange={handleQuickActionSelect}
                        />
                    ) : quickActions.map((action) => {
                        const isSelected = selectedQuickAction === action.key;
                        const isLocked = action.requiresPaidPlan && isFreePlan;
                        const { Icon } = action;
                        const button = (
                            <Button
                                key={action.key}
                                startIcon={<Icon />}
                                onClick={() => handleQuickActionSelect(action.key)}
                                disabled={isLocked}
                                sx={{
                                    color: isSelected ? 'var(--wb-on-primary)' : 'var(--wb-x-text-cccccc, #ccc)',
                                    backgroundColor: isSelected ? 'var(--wb-x-bg-055dff, #055dffff)' : 'transparent',
                                    '&:hover': {
                                        backgroundColor: isSelected ? 'var(--wb-x-bg-2509a3, #2509a3ff)' : 'var(--wb-x-bg-160565b7, #160565b7)'
                                    },
                                    '&.Mui-disabled': {
                                        color: 'var(--wb-x-text-cccccc66, rgba(204,204,204,0.4))'
                                    },
                                    borderRadius: 2,
                                    textTransform: 'none',
                                    px: 2
                                }}
                                type="button"
                                aria-pressed={isSelected}
                            >
                                {action.label}
                            </Button>
                        );

                        if (!action.requiresPaidPlan) {
                            return button;
                        }

                        return (
                            <Tooltip
                                key={action.key}
                                title={isLocked ? 'Se habilita en un plan de pago' : ''}
                            >
                                {/* span envolvente: un Button disabled no dispara eventos de mouse,
                                    y sin este wrapper el Tooltip no se mostraría. */}
                                <span>{button}</span>
                            </Tooltip>
                        );
                    })}
                    {onModelChange && chatModels.length > 0 && (
                        <ChatModelSelector
                            models={chatModels}
                            value={selectedModelId}
                            onChange={onModelChange}
                            disabled={loading}
                        />
                    )}
                </Box>
            </Paper>


            {/* Dialog de detalles */}
            {selectedAB > 0 && answers[selectedAB] && answers[selectedAB - 1] && (
                <AnswerDetailsDialog
                    open={openAnswerDetails}
                    handleClose={handleCloseAnswerDetails}
                    answer={answers[selectedAB]}
                    question={answers[selectedAB - 1]?.query || ""}
                    runningTraces={answers[selectedB]?.runningTraces || []}
                />
            )}

            {/* Comparación/upgrade de planes */}
            <PlanComparisonModal
                open={plansModalOpen}
                onClose={() => setPlansModalOpen(false)}
                accountId={accountId}
                isTenantAdmin={isTenantAdmin}
                isPlatformRoot={isPlatformRoot}
                currentPlanId={planInfo?.plan_id}
                hasActiveSubscription={Boolean(planInfo?.stripe_subscription_id)}
                onPlanChanged={refetchPlanInfo}
            />

            <Snackbar
                open={checkoutToast.open}
                autoHideDuration={6000}
                onClose={() => setCheckoutToast((prev) => ({ ...prev, open: false }))}
                message={checkoutToast.message}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                ContentProps={{
                    sx: {
                        backgroundColor: checkoutToast.severity === 'success' ? 'var(--wb-success-90)' : 'var(--wb-x-bg-3b82f6e6, rgba(59,130,246,0.9))'
                    }
                }}
            />
        </Box>
    );
};

export default memo(OptimizedChatComponent);
